import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Chip, InputAdornment, Skeleton, Tab, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Tabs, TextField, Typography } from '@mui/material';
import { MdSearch } from 'react-icons/md';
import { getData } from '../../../Webservice/Webservice';
import { FONT, authToken, fmtDateTime } from '../Email/emailShared';

const STATUS = {
  sent: { label: 'Received', bg: '#dcfce7', fg: '#166534' },
  failed: { label: 'Not received', bg: '#fee2e2', fg: '#991b1b' },
  skipped: { label: 'Not tried', bg: '#fef3c7', fg: '#92400e' },
};
const PLATFORM = { android: 'Android', ios: 'iPhone', web: 'Web', unknown: '—' };

// Who got the notification, who did not, and the reason, for one campaign.
function PushDelivery({ campaign }) {
  const [tab, setTab] = useState('not_received');
  const [code, setCode] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 350); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData(`push-campaign/${campaign._id}/logs`, { page: page + 1, limit, ...(tab && { status: tab }), ...(code && { code }), ...(debounced && { search: debounced }) }, authToken(), false);
      setData(res);
      setError('');
    } catch {
      setError('Could not load the delivery history.');
    } finally {
      setLoading(false);
    }
  }, [campaign._id, page, limit, tab, code, debounced]);
  useEffect(() => { load(); }, [load]);

  const sum = data?.summary || { received: 0, notReceived: 0, failed: 0, skipped: 0 };
  const noHistory = data && sum.received + sum.notReceived === 0;

  if (['scheduled', 'pending', 'running'].includes(campaign.status)) {
    return <Alert severity="info" sx={{ mt: 2 }}>{campaign.status === 'scheduled' ? 'This has not been sent yet. The delivery history appears after it goes out.' : 'Sending now. The delivery history appears when it finishes.'}</Alert>;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, mb: 1 }}>Delivery history</Typography>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {noHistory && <Alert severity="info" sx={{ mb: 1.5 }}>No per-customer history was recorded for this campaign. It was probably sent before delivery history existed.</Alert>}

      {(data?.reasons || []).length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#64748b', mb: 0.75 }}>Why people did not receive it (click to filter)</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data.reasons.map((r) => (
              <Chip key={r.code} clickable onClick={() => { setCode(code === r.code ? '' : r.code); setTab('not_received'); setPage(0); }} label={`${r.reason} · ${r.count}`}
                sx={{ fontFamily: FONT, fontWeight: 700, height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' }, background: code === r.code ? '#991b1b' : '#fee2e2', color: code === r.code ? '#fff' : '#991b1b' }} />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setCode(''); setPage(0); }} sx={{ minHeight: 40 }}>
          <Tab value="not_received" label={`Not received (${sum.notReceived})`} sx={{ textTransform: 'none', fontWeight: 800, minHeight: 40 }} />
          <Tab value="received" label={`Received (${sum.received})`} sx={{ textTransform: 'none', fontWeight: 800, minHeight: 40 }} />
          <Tab value="" label={`All (${sum.received + sum.notReceived})`} sx={{ textTransform: 'none', fontWeight: 800, minHeight: 40 }} />
        </Tabs>
        <TextField size="small" placeholder="Search name, mobile or email" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
      </Box>

      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'auto', mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              {['Customer', 'Mobile', 'Device', 'Result', 'Reason', 'Time'].map((h) => <TableCell key={h} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12, color: '#64748b' }}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && Array.from({ length: 4 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
            {!loading && (data?.logs || []).length === 0 && (
              <TableRow><TableCell colSpan={6}><Typography sx={{ fontFamily: FONT, textAlign: 'center', color: '#64748b', py: 3 }}>{tab === 'not_received' && !debounced && !code ? 'Everyone received it.' : 'Nothing matches.'}</Typography></TableCell></TableRow>
            )}
            {!loading && (data?.logs || []).map((l) => {
              const st = STATUS[l.status] || STATUS.failed;
              return (
                <TableRow key={l._id} hover>
                  <TableCell sx={{ fontFamily: FONT, fontWeight: 700 }}>{l.customerId?.name || 'Unknown customer'}</TableCell>
                  <TableCell sx={{ fontFamily: FONT }}>{l.customerId?.mobile || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 12.5 }}>{PLATFORM[l.platform] || '—'}{l.tokenTail ? <span style={{ color: '#94a3b8' }}> · …{l.tokenTail}</span> : ''}</TableCell>
                  <TableCell><Chip size="small" label={st.label} sx={{ fontFamily: FONT, fontWeight: 700, background: st.bg, color: st.fg }} /></TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 12.5, minWidth: 220 }} title={l.detail || ''}>{l.status === 'sent' ? '—' : l.reason}</TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDateTime(l.createdAt)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <TablePagination component="div" count={data?.meta?.total || 0} page={page} rowsPerPage={limit} rowsPerPageOptions={[10, 25, 50, 100]} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setLimit(Number(e.target.value)); setPage(0); }} />
    </Box>
  );
}

export default PushDelivery;
